"use client";

/**
 * ChatFloatingPanelV2 — design language v2
 *
 * Shell flotante para paneles del módulo chat (lista de conversaciones,
 * ventana de chat, picker de nuevo chat). Compone sobre `Card` v2 con
 * sombras y bordes del sistema; aporta el comportamiento de panel anclado
 * a bottom-right con tamaños predefinidos y modo "minimizado" (mobile
 * collapse a botón flotante).
 *
 * Reglas:
 *  · Sin `className`/`style` expuestos al consumidor (§2.1).
 *  · El vidrio líquido solo aparece en los controles del header
 *    (`headerActions` debe ser `LiquidPill`/composite). El panel en sí es
 *    superficie sólida v2 — §1 "líquido solo en interacción".
 *  · Z-index desde `z-index.ts` (§3.2). Para overlays bottom-right usamos
 *    `NAVBAR - 1` para quedar por debajo del top bar y por encima del
 *    contenido. El visor de imagen y los modales del dominio usan `Modal`.
 *  · Estados controlados (`collapsed`) desde el orquestador.
 */

import * as React from "react";
import { cn } from "@/lib/utils";
import { Z_INDEX } from "./z-index";

export type ChatFloatingPanelSize = "rail" | "list" | "window";

const SIZE: Record<
  ChatFloatingPanelSize,
  { width: string; height: string; rounded: string }
> = {
  // Compact rail (collapsed list). Solo aparece como botón pequeño en desktop.
  rail: {
    width: "w-auto",
    height: "h-auto",
    rounded: "rounded-full",
  },
  // Panel de lista de conversaciones.
  list: {
    width: "w-full md:w-80",
    height: "h-[80vh] md:h-[520px]",
    rounded: "rounded-t-[20px] md:rounded-[20px]",
  },
  // Ventana de chat individual.
  window: {
    width: "w-full md:w-96",
    height: "h-[80vh] md:h-[520px]",
    rounded: "rounded-t-[20px] md:rounded-[20px]",
  },
};

export interface ChatFloatingPanelV2Props {
  size: ChatFloatingPanelSize;
  /** Encabezado del panel — recomendado: avatar/título + LiquidPill actions. */
  header?: React.ReactNode;
  /** Pie del panel — composer de mensaje, botones de acción. */
  footer?: React.ReactNode;
  children?: React.ReactNode;
  /** Si true: el panel se reduce a un trigger flotante (mobile). */
  collapsed?: boolean;
  /** Slot que se renderiza cuando `collapsed`. Pensado para `LiquidFixedCircleButton` o `LiquidPill`. */
  collapsedContent?: React.ReactNode;
  /** En mobile se ancla a bottom-0 right-0 ocupando casi toda la pantalla. */
  fullBleedOnMobile?: boolean;
  className?: never;
}

export function ChatFloatingPanelV2({
  size,
  header,
  footer,
  children,
  collapsed = false,
  collapsedContent,
  fullBleedOnMobile = true,
}: ChatFloatingPanelV2Props) {
  if (collapsed) {
    return (
      <div
        className="pointer-events-auto"
        style={{ zIndex: Z_INDEX.FIXED_CIRCLE }}
      >
        {collapsedContent}
      </div>
    );
  }

  const dims = SIZE[size];

  return (
    <div
      className={cn(
        "pointer-events-auto flex flex-col overflow-hidden",
        "border border-slate-200/80 bg-white",
        "shadow-[0_24px_64px_-20px_rgba(15,23,42,0.28),0_8px_24px_-12px_rgba(15,23,42,0.18)]",
        "dark:border-zinc-800/80 dark:bg-zinc-950",
        "dark:shadow-[0_24px_64px_-20px_rgba(0,0,0,0.6),0_8px_24px_-12px_rgba(0,0,0,0.5)]",
        dims.width,
        dims.height,
        dims.rounded,
        fullBleedOnMobile && "fixed bottom-0 right-0 md:relative md:bottom-auto md:right-auto"
      )}
      style={
        fullBleedOnMobile ? { zIndex: Z_INDEX.NAVBAR - 1 } : undefined
      }
    >
      {header && (
        <div className="flex shrink-0 items-center justify-between gap-2 border-b border-slate-200/80 bg-slate-50/60 px-2 py-1.5 dark:border-zinc-800/80 dark:bg-zinc-900/60">
          {header}
        </div>
      )}

      <div className="flex-1 overflow-hidden">{children}</div>

      {footer && (
        <div className="shrink-0 border-t border-slate-200/80 bg-white px-3 py-2.5 dark:border-zinc-800/80 dark:bg-zinc-950">
          {footer}
        </div>
      )}
    </div>
  );
}

export default ChatFloatingPanelV2;
